import { useMutation } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { Button } from 'reactstrap';
import { useFormik } from 'formik';
import * as Yup from 'yup';
// Import social media and 2FA icons
import googleIcon from 'assets/images/icons/google.svg';
import facebookIcon from 'assets/images/icons/facebook.svg';
import appleIcon from 'assets/images/icons/apple.svg';
import smsIcon from 'assets/images/icons/sms-notification.svg';
import authenticatorIcon from 'assets/images/icons/authenticator.svg';
import { specificUserRoleData, updateSpecificUserRoleDetails } from 'endpoints/builder/builderDetails.action';

interface EditUserRoleModalProps {
    isOpen: boolean;
    toggle: () => void;
    user: string;
    refetch?: () => void; // Optional refetch function to refresh data after editing
}

const EditUserRoleModal = ({ isOpen, toggle, user, refetch }: EditUserRoleModalProps) => {
    // Local flag to show loader immediately when modal opens
    const [loadingDetails, setLoadingDetails] = useState<boolean>(true);

    // Raw backend status for conditional logic
    const [rawBackendStatus, setRawBackendStatus] = useState<string>('');

    // Status mapping functions
    const mapBackendToFrontend = (backendStatus: string): 'active' | 'suspended' | 'pending' => {
        switch (backendStatus) {
            case 'is_created':
                return 'active';
            case 'suspended':
                return 'suspended';
            case 'disabled':
                return 'suspended'; // Map disabled to suspended on frontend
            case 'pending':
                return 'pending'; // Keep pending as pending
            default:
                return 'active';
        }
    };

    const mapFrontendToBackend = (frontendStatus: 'active' | 'suspended' | 'pending'): string => {
        switch (frontendStatus) {
            case 'active':
                return 'is_created';
            case 'suspended':
                return 'disabled'; // Map suspended to disabled on backend
            case 'pending':
                return 'pending'; // Keep pending as pending
            default:
                return 'is_created';
        }
    };

    // Function to get display status text
    const getDisplayStatus = (backendStatus: string): string => {
        switch (backendStatus) {
            case 'is_created':
                return 'Active';
            case 'suspended':
                return 'Suspended';
            case 'disabled':
                return 'Suspended';
            case 'pending':
                return 'Pending';
            default:
                return 'Unknown';
        }
    };

    // Check if status change should be allowed (only for is_created and disabled)
    const isStatusChangeAllowed = (backendStatus: string): boolean => {
        return backendStatus === 'is_created' || backendStatus === 'disabled';
    };
    // State for social accounts and 2FA data
    const [socialAccounts, setSocialAccounts] = useState({
        is_google_connected: false,
        is_facebook_connected: false,
        is_apple_connected: false
    });
    const [twoFactorAuth, setTwoFactorAuth] = useState({
        text_otp_2fa: false,
        auth_app_2fa: false
    });


    // TypeScript interfaces for form values
    interface BuilderFormValues {
        full_name: string;
        email: string;
        phone_no: string;
        status: 'active' | 'suspended' | 'pending';
    }

    // Validation Schema
    const validationSchema = Yup.object({
        full_name: Yup.string().min(2, 'User name must be at least 2 characters'),
        phone_no: Yup.string()
            .matches(/^[0-9+\-() ]*$/, 'Phone number can only contain digits, spaces, and +-()'),
        status: Yup.string().oneOf(['active', 'suspended', 'pending'], 'Invalid status')
    });

    // Update Builder Mutation
    const updateBuilderMutation = useMutation({
        mutationFn: updateSpecificUserRoleDetails,
        onSuccess: (data) => {
            if (data?.status_code === 200) {
                toast.success('Builder details updated successfully');
                
                // Update the raw backend status if status was changed
                if (isStatusChangeAllowed(rawBackendStatus) && formik.values.status) {
                    const newBackendStatus = mapFrontendToBackend(formik.values.status);
                    setRawBackendStatus(newBackendStatus);
                }
                
                // First close the modal
                // toggle();
                // Then trigger the refetch to update the table data
                if (refetch) {
                    refetch();
                }
            }
        },
        onError: () => {
            toast.error('Error updating builder details');
        }
    });

    // Get Builder Details Mutation
    const builderDetailMutation = useMutation({
        mutationFn: specificUserRoleData,
        onSuccess: (data) => {
            if (data?.status_code === 200 && data?.data) {
                const builderData = data.data;

                // Store raw backend status for conditional logic
                const backendStatus = builderData.Account_status || builderData.status || 'is_created';
                setRawBackendStatus(backendStatus);

                // Set social accounts data
                setSocialAccounts({
                    is_google_connected: builderData.is_google_connected || false,
                    is_facebook_connected: builderData.is_facebook_connected || false,
                    is_apple_connected: builderData.is_apple_connected || false
                });

                // Set 2FA data
                setTwoFactorAuth({
                    text_otp_2fa: builderData.text_otp_2fa || false,
                    auth_app_2fa: builderData.auth_app_2fa || false
                });

                // Set form initial values from API response
                // Only set changeable status for form if status change is allowed
                const formStatusValue = isStatusChangeAllowed(backendStatus) 
                    ? mapBackendToFrontend(backendStatus) 
                    : 'active';
                    
                formik.setValues({
                    full_name: builderData.full_name || '',
                    email: builderData.email || '',
                    phone_no: builderData.phone_no || '',
                    status: formStatusValue
                });
                setLoadingDetails(false);
            }
        },
        onError: () => {
            toast.error('Error fetching builder details');
            setLoadingDetails(false);
        }
    });

    // Formik initialization
    // TypeScript interface for API payload
    interface BuilderUpdatePayload {
        userRoleId: string;
        full_name?: string;
        phone_no?: string;
        status?: string;
    }

    // Handle form submit with proper typing
    const handleSubmit = (values: BuilderFormValues) => {
        // Only include fields that have values, excluding email
        const payload: BuilderUpdatePayload = {
            userRoleId: user,
            ...(values.full_name && { full_name: values.full_name }),
            ...(values.phone_no && { phone_no: values.phone_no })
        };

        // Only include status if it's changeable (not pending)
        if (isStatusChangeAllowed(rawBackendStatus) && values.status) {
            const backendStatus = mapFrontendToBackend(values.status);
            payload.status = backendStatus;
        }

        updateBuilderMutation.mutate(payload);
    };

    const formik = useFormik<BuilderFormValues>({
        initialValues: {
            full_name: '',
            email: '',
            phone_no: '',
            status: 'active' as 'active' | 'suspended' | 'pending'
        },
        validationSchema,
        onSubmit: handleSubmit,
        enableReinitialize: true  // This will allow form to update when initial values change
    });
    useEffect(() => {
        if (isOpen) {
            if (user) {
                builderDetailMutation.mutate(user);
            } else {
                toast.error('User ID is required to fetch builder details');
                setLoadingDetails(false);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, user]);

    // Close modal and clear all form/image values
    const closeAndClear = () => {
        // Reset raw backend status
        setRawBackendStatus('');

        // Reset social accounts and 2FA data
        setSocialAccounts({
            is_google_connected: false,
            is_facebook_connected: false,
            is_apple_connected: false
        });
        setTwoFactorAuth({
            text_otp_2fa: false,
            auth_app_2fa: false
        });

        // clear form values
        formik.setValues({
            full_name: '',
            email: '',
            phone_no: '',
            status: 'active' as 'active' | 'suspended' | 'pending'
        });

        // finally close the modal
        toggle();
    };

    if (!isOpen) return null;

    return (
        <>
            {/* Modal Backdrop */}
            <div
                className="modal-backdrop fade show"
                style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    backgroundColor: 'rgba(0, 0, 0, 0.5)',
                    zIndex: 1040
                }}
                onClick={closeAndClear}
            ></div>

            {/* Modal */}
            <div
                className="modal fade show d-block"
                style={{ zIndex: 1050 }}
                tabIndex={-1}
            >
                <div className="modal-dialog modal-lg modal-dialog-centered">
                    <div className="modal-content">
                        {/* Modal Header */}
                        <div className="modal-header border-bottom-0 pb-0">
                            {/* <h5 className="modal-title fw-semibold">Edit Builder</h5> */}
                            <button
                                type="button"
                                className="btn-close"
                                onClick={closeAndClear}
                                aria-label="Close"
                            ></button>
                        </div>

                        {/* Modal Body */}
                        {(loadingDetails || builderDetailMutation.isPending) ? (
                            <div className="d-flex justify-content-center align-items-center" style={{ height: '70vh' }}>
                                <div className="spinner-border text-primary" role="status">
                                    <span className="visually-hidden">Loading...</span>
                                </div>
                            </div>
                        ) : (
                            <>
                                <div className="modal-body pt-2 pb-2 px-3">
                                    {/* divider */}
                                    {/* <hr className="mb-3" /> */}
                                    {/* Basic Info */}
                                    <div className="fw-semibold mb-0" style={{ fontSize: 17 }}>Basic Info</div>
                                    {/* divider */}
                                    <hr className="mb-4" />
                                    <div className="row gy-3">
                                        <div className="col-md-4">
                                            <div className="mb-2">
                                                <label className="form-label mb-1">User Name</label>
                                                <input
                                                    type="text"
                                                    className={`form-control ${formik.touched.full_name && formik.errors.full_name ? 'is-invalid' : ''}`}
                                                    placeholder='Enter User name'
                                                    {...formik.getFieldProps('full_name')}
                                                />
                                                {formik.touched.full_name && formik.errors.full_name && (
                                                    <div className="invalid-feedback">{formik.errors.full_name}</div>
                                                )}
                                            </div>
                                        </div>
                                        <div className="col-md-4">
                                            <div className="mb-2">
                                                <label className="form-label mb-1">Email</label>
                                                <input
                                                    type="email"
                                                    className="form-control bg-light text-muted"
                                                    value={formik.values.email}
                                                    readOnly
                                                    style={{ cursor: 'not-allowed' }}
                                                />
                                                <small className="text-muted mt-1 d-block" style={{ fontSize: '12px' }}>
                                                    <i className="ri-information-line align-middle me-1"></i>
                                                    Email address cannot be updated
                                                </small>
                                            </div>
                                        </div>
                                        <div className="col-md-4">
                                            <div className="mb-2">
                                                <label className="form-label mb-1">Phone Number</label>
                                                <input
                                                    type="text"
                                                    className={`form-control ${formik.touched.phone_no && formik.errors.phone_no ? 'is-invalid' : ''}`}
                                                    placeholder='Enter phone number'
                                                    {...formik.getFieldProps('phone_no')}
                                                />
                                                {formik.touched.phone_no && formik.errors.phone_no && (
                                                    <div className="invalid-feedback">{formik.errors.phone_no}</div>
                                                )}
                                            </div>
                                        </div>
                                        {/* <div className="col-md-12">
                                            <div className="mb-2">
                                                <label className="form-label mb-1">Location</label>
                                                <input
                                                    type="text"
                                                    className={`form-control ${formik.touched.street_address && formik.errors.street_address ? 'is-invalid' : ''}`}
                                                    placeholder='Enter location'
                                                    {...formik.getFieldProps('street_address')}
                                                />
                                                {formik.touched.street_address && formik.errors.street_address && (
                                                    <div className="invalid-feedback">{formik.errors.street_address}</div>
                                                )}
                                            </div>
                                        </div> */}
                                    </div>

                                    {/* Social Accounts */}
                                    <div className="fw-semibold mt-4 mb-0" style={{ fontSize: 17 }}>Social Accounts</div>
                                    <hr className="mb-4" />
                                    <div className="row align-items-center mb-3">
                                        <div className="col-12">
                                            <div className="d-flex gap-3 align-items-center" style={{ cursor: 'not-allowed' }}>
                                                <div className="d-flex align-items-center gap-2">
                                                    <img
                                                        src={googleIcon}
                                                        alt="Google"
                                                        style={{
                                                            width: '24px',
                                                            height: '24px',
                                                            opacity: socialAccounts.is_google_connected ? 1 : 0.5,
                                                            filter: socialAccounts.is_google_connected ? 'none' : 'grayscale(100%)',
                                                            cursor: 'not-allowed'
                                                        }}
                                                        title={`Google ${socialAccounts.is_google_connected ? 'Connected' : 'Not Connected'}`}
                                                    />
                                                    <span className="text-muted" style={{ fontSize: '14px', cursor: 'not-allowed' }}>
                                                        Google {socialAccounts.is_google_connected ? 'Connected' : 'Not Connected'}
                                                    </span>
                                                </div>
                                                <div className="d-flex align-items-center gap-2">
                                                    <img
                                                        src={facebookIcon}
                                                        alt="Facebook"
                                                        style={{
                                                            width: '24px',
                                                            height: '24px',
                                                            opacity: socialAccounts.is_facebook_connected ? 1 : 0.5,
                                                            filter: socialAccounts.is_facebook_connected ? 'none' : 'grayscale(100%)',
                                                            cursor: 'not-allowed'
                                                        }}
                                                        title={`Facebook ${socialAccounts.is_facebook_connected ? 'Connected' : 'Not Connected'}`}
                                                    />
                                                    <span className="text-muted" style={{ fontSize: '14px', cursor: 'not-allowed' }}>
                                                        Facebook {socialAccounts.is_facebook_connected ? 'Connected' : 'Not Connected'}
                                                    </span>
                                                </div>
                                                <div className="d-flex align-items-center gap-2">
                                                    <img
                                                        src={appleIcon}
                                                        alt="Apple"
                                                        style={{
                                                            width: '24px',
                                                            height: '24px',
                                                            opacity: socialAccounts.is_apple_connected ? 1 : 0.5,
                                                            filter: socialAccounts.is_apple_connected ? 'none' : 'grayscale(100%)',
                                                            cursor: 'not-allowed'
                                                        }}
                                                        title={`Apple ${socialAccounts.is_apple_connected ? 'Connected' : 'Not Connected'}`}
                                                    />
                                                    <span className="text-muted" style={{ fontSize: '14px', cursor: 'not-allowed' }}>
                                                        Apple {socialAccounts.is_apple_connected ? 'Connected' : 'Not Connected'}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* 2FA Setup */}
                                    <div className="fw-semibold mt-4 mb-0" style={{ fontSize: 17 }}>2FA Setup</div>
                                    <hr className="mb-4" />
                                    <div className="row align-items-center mb-3">
                                        <div className="col-12">
                                            <div className="d-flex gap-3 align-items-center" style={{ cursor: 'not-allowed' }}>
                                                <div className="d-flex align-items-center gap-2">
                                                    <img
                                                        src={smsIcon}
                                                        alt="SMS"
                                                        style={{
                                                            width: '24px',
                                                            height: '24px',
                                                            opacity: twoFactorAuth.text_otp_2fa ? 1 : 0.5,
                                                            filter: twoFactorAuth.text_otp_2fa ? 'none' : 'grayscale(100%)',
                                                            cursor: 'not-allowed'
                                                        }}
                                                        title={`SMS 2FA ${twoFactorAuth.text_otp_2fa ? 'Enabled' : 'Disabled'}`}
                                                    />
                                                    <span className="text-muted" style={{ fontSize: '14px', cursor: 'not-allowed' }}>
                                                        SMS 2FA {twoFactorAuth.text_otp_2fa ? 'Enabled' : 'Disabled'}
                                                    </span>
                                                </div>
                                                <div className="d-flex align-items-center gap-2">
                                                    <img
                                                        src={authenticatorIcon}
                                                        alt="Authenticator"
                                                        style={{
                                                            width: '24px',
                                                            height: '24px',
                                                            opacity: twoFactorAuth.auth_app_2fa ? 1 : 0.5,
                                                            filter: twoFactorAuth.auth_app_2fa ? 'none' : 'grayscale(100%)',
                                                            cursor: 'not-allowed'
                                                        }}
                                                        title={`Auth App 2FA ${twoFactorAuth.auth_app_2fa ? 'Enabled' : 'Disabled'}`}
                                                    />
                                                    <span className="text-muted" style={{ fontSize: '14px', cursor: 'not-allowed' }}>
                                                        Auth App 2FA {twoFactorAuth.auth_app_2fa ? 'Enabled' : 'Disabled'}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Builder Status */}
                                    <div className='d-flex justify-content-between align-items-center'>
                                        <div className="fw-semibold mt-4 mb-2" style={{ fontSize: 17 }}>Account Status</div>
                                        <div className={`badge rounded-pill mt-4 ${
                                            rawBackendStatus === 'is_created' ? 'bg-success' : 
                                            rawBackendStatus === 'pending' ? 'bg-warning' : 'bg-danger'
                                        }`}>
                                            {getDisplayStatus(rawBackendStatus)}
                                        </div>
                                    </div>
                                    <hr className="mb-4" />
                                    
                                    {/* Only show status change dropdown for is_created and disabled statuses */}
                                    {isStatusChangeAllowed(rawBackendStatus) && (
                                        <div className="row align-items-center mb-3">
                                            <div className="col-12">
                                                <div className="mb-0">
                                                    <label className="form-label mb-1">Change Status</label>
                                                    <select
                                                        className={`form-select ${formik.touched.status && formik.errors.status ? 'is-invalid' : ''}`}
                                                        style={{
                                                            fontSize: '14px',
                                                            height: '38px'
                                                        }}
                                                        {...formik.getFieldProps('status')}
                                                    >
                                                        <option value="active">Active</option>
                                                        <option value="suspended">Suspended</option>
                                                    </select>
                                                    {formik.touched.status && formik.errors.status && (
                                                        <div className="invalid-feedback">{formik.errors.status}</div>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                    
                                    {/* Show message for pending users */}
                                    {rawBackendStatus === 'pending' && (
                                        <div className="row align-items-center mb-3">
                                            <div className="col-12">
                                                <div className="alert alert-warning mb-0" style={{ fontSize: '14px' }}>
                                                    <i className="ri-information-line me-2"></i>
                                                    This user account is pending activation. Status cannot be changed until the user completes their account setup.
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    {/* Security */}
                                    {/* <div className="fw-semibold mt-4 mb-0" style={{ fontSize: 17 }}>Security</div>
                                    <hr className="mb-4" />
                                    <div className="d-flex flex-row justify-content-end align-items-end gap-3">
                                        <div style={{ flex: 1 }}>
                                            <label className="form-label mb-1" style={{ fontSize: '14px' }}>Update Password</label>
                                            <input
                                                type="password"
                                                className="form-control mb-0"
                                                placeholder='Enter new password'
                                                style={{
                                                    background: '#fff',
                                                    fontSize: '14px',
                                                    height: '38px'
                                                }}
                                            />
                                        </div>
                                        <div>
                                            <Button
                                                color="success"
                                                type="button"
                                                onClick={() => { }}
                                                style={{ width: "110px", height: "38px" }}
                                            >
                                                Update
                                            </Button>
                                        </div>
                                    </div> */}
                                </div>

                                {/* Modal Footer */}
                                <div className="modal-footer border-top-0 pt-2 pb-3 px-3">
                                    <div className="d-flex flex-row justify-content-end align-items-end gap-3">
                                        <Button
                                            color="danger"
                                            type="button"
                                            style={{ width: "110px" }}
                                            onClick={closeAndClear}
                                            disabled={updateBuilderMutation.isPending}
                                        >
                                            Cancel
                                        </Button>
                                        <Button
                                            color="success"
                                            type="button"
                                            style={{ width: "110px" }}
                                            onClick={() => formik.handleSubmit()}
                                            disabled={updateBuilderMutation.isPending || (!formik.values.full_name && !formik.values.email && !formik.values.phone_no && (!isStatusChangeAllowed(rawBackendStatus) || !formik.values.status))}
                                        >
                                            {updateBuilderMutation.isPending ? (
                                                <div className="spinner-border spinner-border-sm" role="status">
                                                    <span className="visually-hidden">Loading...</span>
                                                </div>
                                            ) : (
                                                'Save'
                                            )}
                                        </Button>
                                    </div>
                                </div>
                            </>)}
                    </div>
                </div>
            </div>
        </>
    );
};

export default EditUserRoleModal;